import { createTheme, alpha } from '@mui/material/styles';

// ============================================================
// Light theme — named palette constants (mirrors dark D object)
// ============================================================
const L = {
  bgDefault:     '#f7f7f8',
  bgPaper:       '#FFFFFF',
  textPrimary:   '#0B0D0E',
  textSecondary: '#5A6169',
  textDisabled:  '#73787E',
  border:        '#CDD7E1',
  borderHover:   '#D8D8DF',
  borderDark:    '#434D58',
  primary:       '#0B6BCB',
  primaryHover:  '#185ABC',
  primaryActive: '#0959AA',
};

export const lightTheme = createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
  },
  palette: {
    mode: 'light',
    primary: {
      main: L.primary,
      light: '#42A5F5',
      dark: L.primaryActive,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9C27B0',
      light: '#BA68C8',
      dark: '#7B1FA2',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
      contrastText: '#000000',
    },
    error: {
      main: '#F44336',
      light: '#EF5350',
      dark: '#D32F2F',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#ffffff',
    },
    background: {
      default: L.bgDefault,
      paper: L.bgPaper,
    },
    text: {
      primary: L.textPrimary,
      secondary: L.textSecondary,
      disabled: L.textDisabled,
    },
    divider: L.border,
    grey: {
      50: '#F7F7F8',
      100: '#EBEBEF',
      200: '#CDD7E1',
      300: '#D8D8DF',
      400: '#B0B3B8',
      500: '#9DA0A6',
      600: '#73787E',
      700: '#5A6169',
      800: '#434D58',
      900: '#32383E',
    },
  },
  shadows: [
    'none',
    '0 2px 12px 0 rgba(34, 41, 47, 0.08)',
    '0 4px 16px 0 rgba(34, 41, 47, 0.12)',
    '0 8px 24px 0 rgba(34, 41, 47, 0.16)',
    '0 12px 32px 0 rgba(34, 41, 47, 0.20)',
    ...Array(20).fill('none'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: L.bgDefault,
          color: L.textPrimary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          color: L.textPrimary,
        },
        filled: {
          backgroundColor: L.bgDefault,
        },
        outlined: {
          borderColor: L.border,
          color: L.textSecondary,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: '12px',
          backgroundColor: L.bgPaper,
          border: `1px solid ${L.border}`,
          boxShadow: theme.shadows[1],
          color: L.textPrimary,
          fontFamily: 'Poppins, sans-serif',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          backgroundColor: L.bgPaper,
          border: `1px solid ${L.border}`,
          boxShadow: 'none',
          color: L.textPrimary,
          fontFamily: 'Poppins, sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
          textTransform: 'none',
        },
        contained: {
          backgroundColor: L.primary,
          color: '#ffffff',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: L.primaryHover,
            boxShadow:
              '0 0 #000, 0px 1px 2px 0px rgba(21, 21, 21, 0.08), 0px 2px 4px 0px rgba(21, 21, 21, 0.08)',
          },
          '&:active': {
            backgroundColor: L.primaryActive,
          },
        },
        outlined: {
          borderColor: L.border,
          color: L.textSecondary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: L.bgDefault,
            borderColor: L.borderHover,
          },
        },
        text: {
          color: L.textSecondary,
          '&:hover': {
            backgroundColor: L.bgDefault,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: L.bgPaper,
            '& fieldset': {
              borderColor: L.border,
            },
            '&:hover fieldset': {
              borderColor: L.borderHover,
            },
            '&.Mui-focused fieldset': {
              borderColor: L.primary,
            },
          },
          '& .MuiInputBase-input': {
            color: L.textPrimary,
          },
          '& .MuiInputLabel-root': {
            color: L.textSecondary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: L.bgPaper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: L.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: L.borderHover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: L.primary,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: L.textPrimary,
          backgroundColor: L.bgPaper,
          '&:hover': {
            backgroundColor: L.bgDefault,
          },
          '&.Mui-selected': {
            backgroundColor: L.primary,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: L.primaryHover,
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: 1500,
        },
        paper: {
          backgroundColor: L.bgPaper,
          border: `1px solid ${L.border}`,
          borderRadius: '8px',
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: L.bgPaper,
          borderRadius: '12px',
          border: `1px solid ${L.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'black',
          color: '#fff',
          boxShadow:
            '0 0 #000, 0px 1px 2px 0px rgba(21, 21, 21, 0.08), 0px 2px 4px 0px rgba(21, 21, 21, 0.08)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: L.textPrimary,
        },
        body2: {
          color: L.textSecondary,
        },
        caption: {
          color: L.textDisabled,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: L.border,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: L.primary,
          color: '#ffffff',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: L.bgPaper,
          borderRadius: '12px',
          border: `1px solid ${L.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: L.bgPaper,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: L.bgDefault,
          '& .MuiTableCell-head': {
            color: L.textPrimary,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: L.border,
          color: L.textPrimary,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: L.textSecondary,
          '&:hover': {
            backgroundColor: L.bgDefault,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: L.bgPaper,
          border: `1px solid ${L.border}`,
          borderRadius: '8px !important',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: L.bgDefault,
          borderRadius: '8px',
          '&.Mui-expanded': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: L.textPrimary,
          color: L.bgDefault,
          fontSize: '0.875rem',
          fontWeight: 400,
          borderRadius: '8px',
          padding: '12px 16px',
          border: `1px solid ${L.borderDark}`,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.24)',
          maxWidth: 320,
        },
        arrow: {
          color: L.textPrimary,
          '&:before': {
            border: `1px solid ${L.borderDark}`,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          zIndex: 1500,
        },
      },
    },
  },
});

// ============================================================
// Dark theme — product-posters aesthetic
// Deep blacks, amber accents, subtle glows
// ============================================================
const D = {
  bgDeep:      '#050508',
  bgPrimary:   '#0A0A0F',
  bgCard:      '#0C0C0F',
  bgCardHover: '#1A1A25',
  bgFooter:    '#000000',
  textPrimary: '#FFFFFF',
  textSecondary: '#B0B0BC',
  textMuted:   '#6E6E7A',
  amber:       '#F5A623',
  amberDim:    'rgba(245, 166, 35, 0.15)',
  borderSubtle: 'rgba(255, 255, 255, 0.06)',
  borderAccent: 'rgba(245, 166, 35, 0.25)',
  glowAmber:   'rgba(245, 166, 35, 0.08)',
};

export const darkTheme = createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
  },
  palette: {
    mode: 'dark',
    primary: {
      main: D.amber,
      light: '#f7b84d',
      dark: '#D4891A',
      contrastText: '#050508',
    },
    secondary: {
      main: '#a78bfa',
      light: '#c4b5fd',
      dark: '#7c3aed',
      contrastText: '#050508',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
      contrastText: '#050508',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#050508',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#FFFFFF',
    },
    background: {
      default: D.bgDeep,
      paper: D.bgCard,
    },
    text: {
      primary: D.textPrimary,
      secondary: D.textSecondary,
      disabled: D.textMuted,
    },
    divider: D.borderSubtle,
    grey: {
      50:  '#1A1A25',
      100: '#22222E',
      200: '#2A2A38',
      300: '#353545',
      400: '#4A4A5A',
      500: D.textMuted,
      600: '#8A8A98',
      700: D.textSecondary,
      800: '#D0D0DC',
      900: '#E8E8F0',
    },
    action: {
      hover: 'rgba(255, 255, 255, 0.04)',
      selected: 'rgba(245, 166, 35, 0.08)',
      disabledBackground: 'rgba(255, 255, 255, 0.06)',
      disabled: D.textMuted,
    },
  },
  shadows: [
    'none',
    `0 2px 12px 0 rgba(0, 0, 0, 0.3)`,
    `0 4px 16px 0 rgba(0, 0, 0, 0.4)`,
    `0 8px 24px 0 rgba(0, 0, 0, 0.5)`,
    `0 12px 32px 0 rgba(0, 0, 0, 0.6)`,
    ...Array(20).fill('none'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: D.bgDeep,
          color: D.textPrimary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          color: D.textPrimary,
        },
        filled: {
          backgroundColor: D.bgCardHover,
        },
        outlined: {
          borderColor: D.borderSubtle,
          color: D.textSecondary,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: '12px',
          backgroundColor: 'rgba(12, 12, 15, 0)',
          border: `1px solid ${D.borderSubtle}`,
          boxShadow: theme.shadows[1],
          color: D.textPrimary,
          fontFamily: 'Poppins, sans-serif',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiPaper-root': {
            backgroundColor: `${D.bgCard}`,
          },
          borderRadius: '12px',
          border: `1px solid ${D.borderSubtle}`,
          boxShadow: 'none',
          color: D.textPrimary,
          fontFamily: 'Poppins, sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
          textTransform: 'none',
        },
        contained: {
          backgroundColor: D.amber,
          color: D.bgDeep,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#f7b84d',
            boxShadow: `0 0 16px ${D.glowAmber}`,
          },
          '&:active': {
            backgroundColor: '#D4891A',
          },
        },
        outlined: {
          borderColor: D.borderSubtle,
          color: D.textSecondary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: D.bgCardHover,
            borderColor: D.borderAccent,
          },
        },
        text: {
          color: D.textSecondary,
          '&:hover': {
            backgroundColor: D.bgCardHover,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: D.bgPrimary,
            '& fieldset': {
              borderColor: D.borderSubtle,
            },
            '&:hover fieldset': {
              borderColor: D.borderAccent,
            },
            '&.Mui-focused fieldset': {
              borderColor: D.amber,
            },
          },
          '& .MuiInputBase-input': {
            color: D.textPrimary,
          },
          '& .MuiInputLabel-root': {
            color: D.textMuted,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: D.bgPrimary,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: D.borderSubtle,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: D.borderAccent,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: D.amber,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: D.textPrimary,
          backgroundColor: D.bgCard,
          '&:hover': {
            backgroundColor: D.bgCardHover,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(D.amber, 0.15),
            color: D.amber,
            '&:hover': {
              backgroundColor: alpha(D.amber, 0.2),
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: 1500,
        },
        paper: {
          '&.MuiPaper-root': {
            backgroundColor: `${D.bgCard} !important`,
            backgroundImage: 'none !important',
          },
          border: `1px solid ${D.borderSubtle}`,
          borderRadius: '8px',
          boxShadow: `0 8px 32px rgba(0, 0, 0, 0.5)`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '&.MuiPaper-root': {
            backgroundColor: `${D.bgCard} !important`,
            backgroundImage: 'none !important',
          },
          borderRadius: '12px',
          border: `1px solid ${D.borderSubtle}`,
          boxShadow: `0 8px 32px rgba(0, 0, 0, 0.5)`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: D.bgPrimary,
          color: D.textPrimary,
          borderBottom: `1px solid ${D.borderSubtle}`,
          boxShadow: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: D.textPrimary,
        },
        body2: {
          color: D.textSecondary,
        },
        caption: {
          color: D.textMuted,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: D.borderSubtle,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(D.amber, 0.15),
          color: D.amber,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: D.bgCard,
          borderRadius: '12px',
          border: `1px solid ${D.borderSubtle}`,
          boxShadow: 'none',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: D.bgCard,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: D.bgPrimary,
          '& .MuiTableCell-head': {
            color: D.textSecondary,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: D.borderSubtle,
          color: D.textPrimary,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: D.textSecondary,
          '&:hover': {
            backgroundColor: D.bgCardHover,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: D.bgCard,
          border: `1px solid ${D.borderSubtle}`,
          borderRadius: '8px !important',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: D.bgPrimary,
          borderRadius: '8px',
          '&.Mui-expanded': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: D.bgCardHover,
          color: D.textPrimary,
          fontSize: '0.875rem',
          fontWeight: 400,
          borderRadius: '8px',
          padding: '12px 16px',
          border: `1px solid ${D.borderSubtle}`,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.4)',
          maxWidth: 320,
        },
        arrow: {
          color: D.bgCardHover,
          '&:before': {
            border: `1px solid ${D.borderSubtle}`,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          zIndex: 1500,
        },
        paper: {
          '&.MuiPaper-root': {
            backgroundColor: `${D.bgCard} !important`,
            backgroundImage: 'none !important',
          },
          borderColor: D.borderSubtle,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A2E',
          color: '#FFFFFF',
          border: `1px solid ${D.borderAccent}`,
          borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        },
        message: {
          color: '#FFFFFF',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          fontFamily: 'Poppins, sans-serif',
        },
        filledSuccess: {
          backgroundColor: '#16a34a',
          color: '#FFFFFF',
          border: '1px solid rgba(34, 197, 94, 0.6)',
          '& .MuiAlert-icon': { color: '#FFFFFF' },
        },
        filledError: {
          backgroundColor: '#dc2626',
          color: '#FFFFFF',
          border: '1px solid rgba(239, 68, 68, 0.6)',
          '& .MuiAlert-icon': { color: '#FFFFFF' },
        },
        filledWarning: {
          backgroundColor: '#b45309',
          color: '#FFFFFF',
          border: '1px solid rgba(245, 158, 11, 0.6)',
          '& .MuiAlert-icon': { color: '#FFFFFF' },
        },
        filledInfo: {
          backgroundColor: '#1d4ed8',
          color: '#FFFFFF',
          border: '1px solid rgba(59, 130, 246, 0.6)',
          '& .MuiAlert-icon': { color: '#FFFFFF' },
        },
        standardSuccess: {
          backgroundColor: 'rgba(22, 163, 74, 0.15)',
          color: '#4ade80',
          border: '1px solid rgba(22, 163, 74, 0.4)',
          '& .MuiAlert-icon': { color: '#4ade80' },
        },
        standardError: {
          backgroundColor: 'rgba(220, 38, 38, 0.15)',
          color: '#f87171',
          border: '1px solid rgba(220, 38, 38, 0.4)',
          '& .MuiAlert-icon': { color: '#f87171' },
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          color: '#fbbf24',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          '& .MuiAlert-icon': { color: '#fbbf24' },
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          color: '#93c5fd',
          border: '1px solid rgba(59, 130, 246, 0.4)',
          '& .MuiAlert-icon': { color: '#93c5fd' },
        },
        message: {
          color: 'inherit',
        },
        action: {
          color: 'inherit',
        },
      },
    },
  },
});
